import React, { useState } from 'react';
import {
  ListBase, ListView, TextField, FunctionField, TextInput, SelectInput, useTranslate,
} from 'react-admin';
import { Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '@/utils/httpClient';
import {
  FilterToggle, ListScreen, PageTitle, RenameRowActions, ResponsiveDatagrid, SectionHeader,
  useActiveFilterCount,
} from '@/components/shell';

interface KycFieldRecord {
  id: number;
  name: string;
  usedByFields?: string[];
}

const VISIBLE_USES = 4;

/** The standard fields that read this KYC field, or a note that none does. */
const UsedBy: React.FC<{ record: KycFieldRecord }> = ({ record }) => {
  const t = useTranslate();
  const theme = useTheme();
  const fields = record?.usedByFields ?? [];
  if (!fields.length) {
    return (
      <Typography variant="body2" color="text.secondary" component="span">
        {t('resources.kyc-fields.fields.unused')}
      </Typography>
    );
  }
  const rest = fields.length - VISIBLE_USES;
  return (
    <Typography variant="body2" color="text.secondary" component="span">
      <Typography component="span" variant="body2" sx={{ fontFamily: theme.custom.text.mono }}>
        {fields.slice(0, VISIBLE_USES).join(', ')}
      </Typography>
      {rest > 0 ? ` ${t('common.more', { count: rest })}` : ''}
    </Typography>
  );
};

const KycFieldActions: React.FC<{ record: KycFieldRecord }> = ({ record }) => {
  const t = useTranslate();
  return (
    <RenameRowActions
      name={record?.name || ''}
      nameLabel={t('resources.kyc-fields.fields.name')}
      onRename={(name) => httpClient.put('/admin/kyc-field/UpdateKYCField', { id: record.id, name })}
      onDelete={() => httpClient.delete(`/admin/kyc-field/DeleteKYCField/${record.id}`)}
    />
  );
};

/** Rendered inside the list's context so the filter badge counts the active filters. */
const Header: React.FC<{ filtersOpen: boolean; onToggleFilters: () => void }> = ({ filtersOpen, onToggleFilters }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const activeCount = useActiveFilterCount();
  return (
    <SectionHeader
      left={
        <PageTitle
          title={t('resources.kyc-fields.name', { smart_count: 2 })}
          subtitle={t('screens.kycFields.subtitle')}
        />
      }
      filters={<FilterToggle open={filtersOpen} onToggle={onToggleFilters} activeCount={activeCount} />}
      create={{ label: t('common.create'), onClick: () => navigate('/kyc-fields/create') }}
    />
  );
};

export const KycFieldList = () => {
  const t = useTranslate();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = [
    <TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />,
    ...(filtersOpen
      ? [
          <SelectInput
            key="usage"
            source="Usage"
            label={t('resources.kyc-fields.fields.usageCount')}
            choices={[
              { id: 'used', name: t('screens.kycFields.used') },
              { id: 'unused', name: t('resources.kyc-fields.fields.unused') },
            ]}
            alwaysOn
            emptyText={t('common.filters.all')}
          />,
        ]
      : []),
  ];

  return (
    <ListBase perPage={25} sort={{ field: 'name', order: 'ASC' }}>
      <ListScreen header={<Header filtersOpen={filtersOpen} onToggleFilters={() => setFiltersOpen((o) => !o)} />}>
        <ListView filters={filters} actions={false} empty={false}>
          <ResponsiveDatagrid<KycFieldRecord>
            rowClick={false}
            mobile={{
              primary: (r) => r.name,
              lines: [{ label: t('screens.kycFields.usedBy'), render: (r) => <UsedBy record={r} /> }],
              actions: (r) => <KycFieldActions record={r} />,
            }}
          >
            <TextField source="name" label={t('resources.kyc-fields.fields.name')} sortBy="name" />
            <FunctionField
              label={t('screens.kycFields.usedBy')}
              sortable={false}
              render={(r: KycFieldRecord) => <UsedBy record={r} />}
            />
            <FunctionField
              label=""
              textAlign="right"
              sortable={false}
              render={(r: KycFieldRecord) => <KycFieldActions record={r} />}
            />
          </ResponsiveDatagrid>
        </ListView>
      </ListScreen>
    </ListBase>
  );
};
